import { IVariableOperationsGpsBonusSetup } from "@/PayPlans/types/sections/variableOperationsGpsBonus";
import { ESpiffTypes, IEligibilityRequirement, ISpiffSetupRule, ITiers } from "../../types/spiffSetup";
import { BuildVariableOperationsGpsBonusSetup } from "@/PayPlans/helpers/DefaultSections";
import { allowedGPSKeys, allowedSalesTradesKeys, allowedDepartmentKeys, allowedOpCodesKeys, filterByInterface } from "./utils";
import { IOpCode } from "@/PayPlans/types/sections/technicians";
import { cloneDeep } from "lodash";

const INITIAL_RULE = {
	_id: "",
	make: "",
	model: "",
	trim: "",
	status: [],
	rangeType: "",
	startYear: "",
	endYear: "",
	stockExpression: "",
};

const INITIAL_TIER = {
	from: {
		input: "Unit",
		value: 0,
	},
	to: {
		input: "Unit",
		value: 10000,
	},
	commissions: [
		{
			from: {
				input: "Dollar",
				value: -10000000,
			},
			to: {
				input: "Dollar",
				value: 10000000,
			},
			commission: {
				input: "Dollar",
				value: 0,
			},
		},
	],
};

const INITIAL_STATE: IEligibilityRequirement = {
	rules: [INITIAL_RULE],
	tiers: [INITIAL_TIER],
	applyTiersRetroactively: false,
	genericValue: "",
	pvrType: "",
	franchises: [],
	component: "",
};

const INITIAL_TIER_DEP = {
	from: { input: "Duration", value: 0 },
	to: { input: "Duration", value: 744 },
	commissions: [
		{
			from: { input: "Unit", value: 0 },
			to: { input: "Unit", value: 744 },
			commission: { input: "Duration", value: 0 },
		},
	],
};

const INITIAL_STATE_DEP: IEligibilityRequirement = {
	tiers: [INITIAL_TIER_DEP],
	applyTiersRetroactively: false,
	selectedPayType: ["Customer"],
	selectedDepartment: [],
	component: "LABOR",
};

const INITIAL_TIER_OP_CODES = {
	from: { input: "Duration", value: 0 },
	to: { input: "Duration", value: 744 },
	commissions: [
		{
			from: { input: "Unit", value: 744 },
			to: { input: "Unit", value: 0 },
			commission: { input: "Duration", value: 0 },
		},
	],
};

const INITIAL_STATE_OP_CODES: IEligibilityRequirement = {
	tiers: [INITIAL_TIER_OP_CODES],
	applyTiersRetroactively: false,
	selectedPayType: ["Customer"],
	selectedOperationCodes: [
		{
			code: "",
			type: "Generic",
			pay: ["Customer"],
		},
	],
	selectedTimeLapse: "",
	component: "LABOR",
};

type TPayload = {
	index?: number;
	rule?: ISpiffSetupRule;
	tiers?: ITiers[];
	pvrType?: string;
	requiredStoreUnits?: number;
	requiredPvr?: number | null;
	applyTiersRetroactively?: boolean;
	franchises?: string[];
	selectedPayType?: string[];
	selectedOperationCodes?: IOpCode[];
	selectedTimeLapse?: string;
	selectedDepartment?: string[];
	component?: string;
};

type TResetPayload = { franchises: string[]; spiffType: string };

type TAction =
	| { type: EActionType.RESET; payload: TResetPayload }
	| { type: EActionType.ADD_RULE; payload: TPayload }
	| { type: EActionType.UPDATE_RULE; payload: TPayload }
	| { type: EActionType.DELETE_RULE; payload: TPayload }
	| { type: EActionType.UPDATE_TIER; payload: TPayload }
	| { type: EActionType.UPDATE_PVR_TYPE; payload: TPayload }
	| { type: EActionType.UPDATE_APPLY_RETROACTIVELY; payload: TPayload }
	| { type: EActionType.UPDATE_FRANCHISES; payload: TPayload }
	| { type: EActionType.UPDATE_ALL; payload: TPayload | IVariableOperationsGpsBonusSetup }
	| { type: EActionType.UPDATE_GPS_COMPLIANCE; payload: Partial<IVariableOperationsGpsBonusSetup> };

enum EActionType {
	ADD_RULE = "add_rule",
	UPDATE_RULE = "update_rule",
	DELETE_RULE = "delete_rule",
	UPDATE_TIER = "update_tier",
	UPDATE_PVR_TYPE = "update_pvrType",
	UPDATE_APPLY_RETROACTIVELY = "update_apply_retroactively",
	RESET = "reset",
	UPDATE_FRANCHISES = "update_franchises",
	UPDATE_ALL = "update_all",
	UPDATE_GPS_COMPLIANCE = "update_gps_compliance",
}

const getRulesAndTiers = (state: IEligibilityRequirement) => {
	const rules = state?.rules?.length ? state?.rules : [INITIAL_RULE];
	const tiers = state?.tiers?.length ? state?.tiers : [INITIAL_TIER];
	return { rules, tiers };
};

const handleReset = (
	state: IEligibilityRequirement | IVariableOperationsGpsBonusSetup,
	action: TAction
): IEligibilityRequirement | IVariableOperationsGpsBonusSetup => {
	if (action.type === EActionType.RESET && action.payload.spiffType === ESpiffTypes.GPS_COMPLIANCE) {
		return filterByInterface(
			{ ...BuildVariableOperationsGpsBonusSetup({ franchises: action.payload.franchises }), ...state },
			allowedGPSKeys
		);
	} else if (action.type === EActionType.RESET && action.payload.spiffType === ESpiffTypes.DEPARTMENT) {
		const changeState = (state as IEligibilityRequirement).selectedDepartment?.length ? state : {};
		return filterByInterface({ ...cloneDeep(INITIAL_STATE_DEP), ...changeState }, allowedDepartmentKeys);
	} else if (action.type === EActionType.RESET && action.payload.spiffType === ESpiffTypes.OP_CODES) {
		const changeState = (state as IEligibilityRequirement).selectedOperationCodes?.length ? state : {};
		return filterByInterface({ ...cloneDeep(INITIAL_STATE_OP_CODES), ...changeState }, allowedOpCodesKeys);
	} else {
		const { rules, tiers } = getRulesAndTiers(state as IEligibilityRequirement);
		return filterByInterface({ ...state, rules, tiers, genericValue: "" }, allowedSalesTradesKeys);
	}
};

const handleAddRule = (state: IEligibilityRequirement, action: TAction) => {
	if (action.type === EActionType.ADD_RULE && action.payload?.rule) {
		return { ...state, rules: [...(state.rules ?? []), action.payload.rule] };
	}
	return state;
};

const handleUpdateRule = (state: IEligibilityRequirement, action: TAction) => {
	if (action.type === EActionType.UPDATE_RULE && action.payload?.rule) {
		return {
			...state,
			rules: state.rules?.length
				? state.rules
						.map((rule, index) => (index === action.payload.index ? action.payload.rule : rule))
						.filter((rule): rule is ISpiffSetupRule => rule !== undefined)
				: [],
		};
	}
	return state;
};

const handleDeleteRule = (state: IEligibilityRequirement, action: TAction) => {
	if (action.type === EActionType.DELETE_RULE && action.payload?.index !== undefined) {
		const rules = state.rules?.length ? state.rules.filter((_rule, ruleIndex) => ruleIndex !== action.payload.index) : [];

		if (rules.length === 0) {
			rules.push(INITIAL_RULE);
		}

		return { ...state, rules };
	}
	return state;
};

const handleUpdateTier = (state: IEligibilityRequirement, action: TAction) => {
	if (action.type === EActionType.UPDATE_TIER && action.payload?.tiers) {
		return { ...state, tiers: action.payload.tiers };
	}
	return state;
};

const handleUpdatePvrType = (state: IEligibilityRequirement, action: TAction) => {
	if (action.type === EActionType.UPDATE_PVR_TYPE) {
		const { pvrType, tiers } = action.payload || {};
		return {
			...state,
			...(pvrType && { pvrType }),
			...(tiers && { tiers }),
		};
	}
	return state;
};

const handleUpdateApplyRetroactively = (state: IEligibilityRequirement, action: TAction) => {
	if (action.type === EActionType.UPDATE_APPLY_RETROACTIVELY && action.payload?.applyTiersRetroactively !== undefined) {
		return { ...state, applyTiersRetroactively: action.payload.applyTiersRetroactively };
	}
	return state;
};

const handleUpdateFranchises = (state: IEligibilityRequirement, action: TAction) => {
	if (action.type === EActionType.UPDATE_FRANCHISES && action.payload?.franchises) {
		return { ...state, franchises: action.payload.franchises };
	}
	return state;
};

const handleUpdateAll = (state: IEligibilityRequirement | IVariableOperationsGpsBonusSetup, action: TAction) => {
	if (action.type === EActionType.UPDATE_ALL && action.payload) {
		return { ...state, ...action.payload };
	}
	return state;
};

const handleGpsCompliance = (state: IVariableOperationsGpsBonusSetup, action: TAction) => {
	if (action.type === EActionType.UPDATE_GPS_COMPLIANCE && action.payload) {
		return { ...state, ...action.payload };
	}
	return state;
};

const eligibilityRequirementsReducer = (
	state: IEligibilityRequirement | IVariableOperationsGpsBonusSetup,
	action: TAction
): IEligibilityRequirement | IVariableOperationsGpsBonusSetup => {
	switch (action.type) {
		case EActionType.RESET:
			return handleReset(state as IEligibilityRequirement, action);
		case EActionType.ADD_RULE:
			return handleAddRule(state as IEligibilityRequirement, action);
		case EActionType.UPDATE_RULE:
			return handleUpdateRule(state as IEligibilityRequirement, action);
		case EActionType.DELETE_RULE:
			return handleDeleteRule(state as IEligibilityRequirement, action);
		case EActionType.UPDATE_TIER:
			return handleUpdateTier(state as IEligibilityRequirement, action);
		case EActionType.UPDATE_PVR_TYPE:
			return handleUpdatePvrType(state as IEligibilityRequirement, action);
		case EActionType.UPDATE_APPLY_RETROACTIVELY:
			return handleUpdateApplyRetroactively(state as IEligibilityRequirement, action);
		case EActionType.UPDATE_FRANCHISES:
			return handleUpdateFranchises(state as IEligibilityRequirement, action);
		case EActionType.UPDATE_ALL:
			return handleUpdateAll(state, action);
		case EActionType.UPDATE_GPS_COMPLIANCE:
			return handleGpsCompliance(state as IVariableOperationsGpsBonusSetup, action);
		default:
			return state;
	}
};

export {
	eligibilityRequirementsReducer,
	INITIAL_STATE,
	INITIAL_RULE,
	INITIAL_TIER,
	INITIAL_TIER_DEP,
	INITIAL_STATE_DEP,
	INITIAL_TIER_OP_CODES,
	INITIAL_STATE_OP_CODES,
	EActionType,
};
export type { TAction };
